import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import PetSearch from '../PetSearch';
import ServiceSelector from '../ServiceSelector';
import { NewBookingAlert } from '@spike/new-booking-model';
import { useApiClientWrapper, useAuth } from 'hooks';
import Pet from '@spike/pet-model';
import Staff from 'model/Staff';
import { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchAlertsThunk } from '@spike/new-bookings-action';
import DateSelector from '../DateSelector';
import { Discount as DiscountModel, PetServiceWrapper, versionPetAndServices } from 'model/NewMultipleBooking';
import StaffSelector from '../StaffSelector/StaffSelector';
import Discount from '../Discount';

interface Props {
    parentId: number;
    petId?: number;
    service?: PetServiceWrapper;
    staffId?: number;
    staffIdPreselected?: number;
    versionPetAndServices?: versionPetAndServices;
    date?: Moment;
    petsUsed?: Array<number>;
    discount: DiscountModel;
    servicesUsed?: Array<number>;
    petWithoutSelect?: boolean;
    defaultDiscount?: string;
    onChangeDate: (value: Moment) => void;
    onChangePet: (value: Pet) => void;
    onChangeService: (value: PetServiceWrapper, data: boolean) => void;
    onChangeStaff: (value?: Staff) => void;
    onChangeDiscount: (discount: DiscountModel) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {},
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        }
    })
);

export const AddOrEditService: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const auth = useAuth();
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const [showService, setShowService] = useState(false);
    const [showStaff, setShowStaff] = useState(false);
    const [date, setDate] = useState<Moment | undefined>(props.date);

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);

    useEffect(() => {
        if (date) {
            dispatch(
                fetchAlertsThunk(
                    apiClientWrapper,
                    props.parentId || 0,
                    props.petId || 0,
                    date.clone().tz('utc'),
                    false,
                    props.service?.service.id,
                    props.staffId,
                    undefined
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.date, date, props.service, props.staffId]);

    useEffect(() => {
        if (props.service) {
            setShowStaff(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.service]);

    useEffect(() => {
        if (date) {
            props.onChangeDate(date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlerPet = (value: Pet) => {
        setShowService(true);
        if (
            props.versionPetAndServices === versionPetAndServices.add ||
            props.versionPetAndServices === versionPetAndServices.another
        ) {
            setShowStaff(false);
        }
        props.onChangePet(value);
    };

    const handlerChangeDate = (value: Moment) => {
        setDate(value);
        props.onChangeDate(value);
    };

    const handlerService = (value: PetServiceWrapper, data: boolean) => {
        setShowStaff(true);
        props.onChangeService(value, data);
    };

    const handlerStaff = (staffSelected?: Staff) => {
        props.onChangeStaff(staffSelected);
    };

    const petsView = (
        <Box className={classes.sectionContainer}>
            <PetSearch
                parentID={props.parentId}
                petId={props.petId}
                multiplePets={true}
                petsUsed={props.petsUsed}
                readOnly={props.versionPetAndServices !== versionPetAndServices.another}
                hideSelect={props.petWithoutSelect}
                handlerPet={handlerPet}
            />
        </Box>
    );

    const dateView = (
        <Box className={classes.sectionContainer}>
            <DateSelector
                date={date!}
                isRecurrent={false}
                withoutBorder
                showAlertBusinessHour={
                    alerts && alerts?.filter(alert => alert.alertType === 'date_time')?.length > 0 ? true : false
                }
                multiplePet={true}
                onChangeFrequencyDuration={() => {}}
                onChangeRecurrent={() => {}}
                onChangeDate={handlerChangeDate}
                alerts={alerts?.filter(alert => alert.alertType === 'unified')}
            />
        </Box>
    );

    const serviceView = (
        <Box className={classes.sectionContainer}>
            <ServiceSelector
                customizable
                selectedService={props.service}
                servicesSelected={props.servicesUsed || []}
                clientId={props.parentId!}
                petId={props.petId!}
                multiplePets={true}
                onSelectService={handlerService}
            />
        </Box>
    );

    const discountView = (
        <Box className={classes.sectionContainer}>
            <Discount
                discount={props.discount}
                max={
                    props.discount?.type === 'percentage'
                        ? 100
                        : props.discount?.type === 'amount'
                        ? props.service?.price
                        : 0.5
                }
                default={props.defaultDiscount}
                onChangeDiscount={props.onChangeDiscount}
            />
        </Box>
    );

    const staffView = (
        <Box className={classes.sectionContainer}>
            <StaffSelector
                selectedStaff={props.staffId || props.staffIdPreselected || auth.user!.staffId}
                service={props.service}
                onSelectStaff={handlerStaff}
                alerts={alerts?.filter(alert => alert.alertType === 'staff')}
            />
        </Box>
    );

    return (
        <Box className={classes.container}>
            {petsView}
            {showService && dateView}
            {showService && serviceView}
            {showStaff && discountView}
            {showStaff && staffView}
        </Box>
    );
};

export default AddOrEditService;
